import React, { createContext, useContext, useState } from 'react';

// API 기본 URL
const API_URL = process.env.REACT_APP_API_URL;

// 초기 상태
const initialState = {
  isLoading: false,
  error: null,
};

// Context 생성
const ApiContext = createContext(initialState);

// Custom hook을 만들어 Context 사용을 더 쉽게 함
export const useApiContext = () => {
  return useContext(ApiContext);
};

// Provider 컴포넌트
export const ApiProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 일반적인 fetch 래퍼 함수
  const fetchData = async (endpoint, options = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
      const response = await fetch(url, options);
      
      // 응답 본문 파싱
      let responseData;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        responseData = null;
      }
      
      // 응답이 정상이 아닌 경우
      if (!response.ok) {
        let errorMessage = '요청 처리 중 오류가 발생했습니다.';
        let retry = false;
        
        // 상태 코드별 에러 메시지 처리
        switch (response.status) {
          case 504:
            errorMessage = '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
            retry = true;
            break;
          case 503:
            errorMessage = '서버가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.';
            retry = true;
            break;
          case 400:
            errorMessage = responseData?.message || '잘못된 요청입니다.';
            break;
          default:
            errorMessage = responseData?.message || `API 요청 실패: ${response.status}`;
        }
        
        // 백엔드에서 retry 플래그를 명시적으로 보낸 경우 우선 적용
        if (responseData && typeof responseData.retry !== 'undefined') {
          retry = responseData.retry;
        }
        
        const error = new Error(errorMessage);
        error.retry = retry;
        error.status = response.status;
        error.data = responseData;
        throw error;
      }
      
      setIsLoading(false);
      return { data: responseData };
    } catch (error) {
      const errorMessage = error.message || '알 수 없는 오류가 발생했습니다.';
      const errorObj = {
        message: errorMessage,
        retry: error.retry || false,
        status: error.status,
        data: error.data
      };
      setError(errorMessage);
      setIsLoading(false);
      return { error: errorObj };
    }
  };

  // GET 요청
  const get = async (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return fetchData(url);
  };

  // POST 요청
  const post = async (endpoint, body) => {
    return fetchData(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  };

  // 아파트 자동완성
  const getAutocomplete = async (query) => {
    if (!query || query.length === 0) return { data: [] };
    return get(`/autocomplete`, { q: query });
  };

  // 아파트 검색
  const searchApt = async (searchAptName) => {
    return post('/search_apt', { searchAptName });
  };

  // 아파트 정보 조회
  const getAptInfo = async (aptData) => {
    const bodyData = typeof aptData === 'string' 
      ? { aptNumber1: aptData, aptNumber2: "", aptNumber3: "" } 
      : aptData;
    
    return post('/apt', { data: bodyData });
  };

  // 아파트 매물 정보 조회
  const getAptSaleInfo = async (apt_no, pyeongNo, isRent) => {
    return post('/apt_sale_info', { apt_no, pyeongNo, isRent });
  };

  // 아파트 메트릭스 정보 조회
  const getAptMetrixInfo = async (regionInfo) => {
    // 다중 선택 여부에 따라 API 요청 본문 구성
    let requestBody;
    
    // 다중 선택 케이스
    if (regionInfo.multipleSelect && Array.isArray(regionInfo.cityCode)) {
      requestBody = {
        data: { 
          regionCode: regionInfo.regionCode || "",
          regionName: regionInfo.regionName || "",
          cityCodes: regionInfo.cityCode,  // 배열
          cityNames: regionInfo.cityName,  // 배열
          multipleSelect: true
        }
      };
    } 
    // 단일 선택 또는 전체 선택 케이스
    else {
      requestBody = {
        data: { 
          regionCode: regionInfo.regionCode || "",
          regionName: regionInfo.regionName || "",
          cityCode: regionInfo.cityCode || "",
          cityName: regionInfo.cityName || ""
        }
      };
    }
    
    return post('/api/aptmetrix', requestBody);
  };

  // 지역별 아파트 정보 조회
  const getComplexes = async (cortarCode) => {
    return get(`/complexes/${cortarCode}`);
  };

  // 인기 아파트 조회
  const getPopularApt = async () => {
    return get('/popular/apt');
  };

  // 인기 지역 조회
  const getPopularArea = async () => {
    return get('/popular/area');
  };

  // 가격 변동 순위 조회
  const getPriceGapRank = async (params) => {
    return get('/popular/price_gap_rank', params);
  };

  // 거래량 순위 조회
  const getTradingVolumeRank = async (params) => {
    return get('/popular/trading_volume_rank', params);
  };

  // 구독 취소 요청
  const requestCancelSubscription = async (userId) => {
    return post('/request-cancel', { userId });
  };

  // 무료 체험 확인
  const checkFreeTrial = async (userId) => {
    return get(`/check-free-trial/${userId}`);
  };

  // 무료 체험 활성화
  const activateFreeTrial = async (userId) => {
    return post('/activate-free-trial', { userId });
  };

  // 결제 세션 생성
  const createCheckoutSession = async (priceId, userId, planName, price) => {
    return post('/create-checkout-session', { 
      priceId, 
      userId, 
      planName,
      price 
    });
  };

  // 구독 확인
  const verifySubscription = async (sessionId, userId) => {
    return post('/verify-subscription', { 
      sessionId,
      userId
    });
  };

  // 문의 메시지 전송
  const sendContactMessage = async (webhookUrl, formData) => {
    return fetch(webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        message: formData.message
      })
    });
  };

  // Vanna API - 자연어 질의
  const queryVanna = async (question) => {
    return post('/vanna/query', { question });
  };

  // Vanna API - 서버 상태 확인
  const getVannaHealth = async () => {
    return get('/vanna/health');
  };

  // 아파트 필터링 검색
  const filterApartments = async (filters) => {
    const requestBody = { filters };
    return post('/api/filter-apartments', requestBody);
  };

  // API 함수를 객체로 묶어서 제공
  const api = {
    getAutocomplete,
    searchApt,
    getAptInfo,
    getAptSaleInfo,
    getAptMetrixInfo,
    getComplexes,
    getPopularApt,
    getPopularArea,
    getPriceGapRank,
    getTradingVolumeRank,
    requestCancelSubscription,
    checkFreeTrial,
    activateFreeTrial,
    createCheckoutSession,
    verifySubscription,
    sendContactMessage,
    // Vanna API
    queryVanna,
    getVannaHealth,
    // Filter API
    filterApartments
  };

  // Context에 제공할 값
  const contextValue = {
    isLoading,
    error,
    api,
    // 더 낮은 레벨의 API를 직접 사용해야 할 경우를 위해 제공
    get,
    post,
    fetchData
  };

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
}; 